import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IdentificationTypeEnum } from '@core/enums/user';
import { IData } from '@core/interfaces/data.interface';
import { endPoints } from '@core/utils/constants';
import { BehaviorSubject, catchError, map, of, tap } from 'rxjs';
import { API_URL } from './tokens/api-url.token';

@Injectable({ providedIn: 'root' })
export class UserService {

  private readonly usuarioActivoSubject = new BehaviorSubject<any>(null);
  usuarioActivo$ = this.usuarioActivoSubject.asObservable();
  //filtros
  private readonly filtrosActivosSubject = new BehaviorSubject<any>({});
  filtrosActivos$ = this.filtrosActivosSubject.asObservable();

  // Listado de usuarios
  private readonly usuariosSubject = new BehaviorSubject<any[]>([]);
  usuarios$ = this.usuariosSubject.asObservable();

  constructor(
    @Inject(API_URL) private readonly apiUrl: string,
    private readonly http: HttpClient
  ) { }

  //Setter
  setUsuarioActivo(usuario: any) {
    this.usuarioActivoSubject.next(usuario);
  }

  getUsuarioActivo() {
    return this.usuarioActivoSubject.value;
  }

  setfiltrosActivos(filtros: any) {
    this.filtrosActivosSubject.next(filtros);
  }

  getfiltrosActivos() {
    return this.filtrosActivosSubject.value;
  }

  setUsuarios(usuarios: any[]) {
    this.usuariosSubject.next(usuarios);
  }

  getUsuarios() {
    return this.usuariosSubject.value;
  }

  // Métodos para roles
  getCurrentUserRoleId(): number | null {
    return this.usuarioActivoSubject.value?.rolId || null;
  }

  getCurrentUserRoleName(): string | null {
    const roleId = this.getCurrentUserRoleId();
    
    if(roleId == 1){
      return 'Administrador';
    }

    return this.usuarioActivoSubject.value?.perfil || '';
  }

  hasRole(roleId: number): boolean {
    return this.getCurrentUserRoleId() === roleId;
  }

  hasRoleName(rol: string): boolean {
    const userRol = this.getCurrentUserRoleName();
    return userRol? userRol == rol : false;
  }

  // Método para verificar múltiples roles
  hasAnyRole(roleIds: number[]): boolean {
    const userRoleId = this.getCurrentUserRoleId();
    return userRoleId ? roleIds.includes(userRoleId) : false;
  }

  hasAnyRoleName(roles: string[]): boolean {
    const userRole = this.getCurrentUserRoleName() as string;
    return userRole ? roles.includes(userRole) : false;
  }

  //Apis
  validateIdentification(numeroIdentificacion: string) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/usuario/datos-ciudadano/validar`;
    const body = {
      numeroIdentificacion,
      tipoIdentificacion: IdentificationTypeEnum.Cedula
    };

    return this.http.post(url, body);
  }

  /**
   * @returns valor de **usuarioId**
   */
  registerUser(model: FormData) {
    const url = `${this.apiUrl}/${endPoints.EXTERNAL}/usuario/registrar`;

    return this.http.post<IData<any>>(url, model);
  }

  updateUser(model: FormData) {
    const url = `${this.apiUrl}/${endPoints.EXTERNAL}/usuario/actualizar`;

    return this.http.post<IData<any>>(url, model);
  }

  changeStatusUser(model: FormData, estado: string) {
    const url = `${this.apiUrl}/${endPoints.EXTERNAL}/usuario/actualizar`;

    return this.http.post<IData<number>>(url, model).pipe(
      tap(() => {
        // Actualizar el listado local después de eliminar
        const usuarioId = model.get('usuarioId');
        const usuariosActuales = this.usuariosSubject.value;
        const nuevosUsuarios = usuariosActuales.map(usuario =>
          usuario.usuarioId === usuarioId ? { ...usuario, estado: estado } : usuario
        );
        this.usuariosSubject.next(nuevosUsuarios);
      })
    );
  }

  registerCognitoUser(model: FormData) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/usuario/registrar/cognito`;

    return this.http.post(url, model);
  }

  getUsuariosByModel(model: any = {}) {
    const url = `${this.apiUrl}/${endPoints.EXTERNAL}/usuario/usuarios`;

    return this.http.post<any>(url, model).pipe(
      map(({ data }) => {
        this.usuariosSubject.next(data);
        return data;
      }),
      catchError(() => {
        this.usuariosSubject.next([]);
        return of([]);
      })
    );
  }

  getUsuarioById(usuarioId: string) {
    const url = `${this.apiUrl}/${endPoints.EXTERNAL}/usuario/${usuarioId}`;
    return this.http.get<any>(url).pipe(
      map(({ data }) => data[0]),//get first
      catchError(() => {
        return of([]);
      })
    );
  }

  getRoles() {
    const url = `${this.apiUrl}/${endPoints.EXTERNAL}/usuario/roles`;
    return this.http.get<any>(url).pipe(
      map(({ data }) => data),
      catchError(() => of([]))
    );
  }

  getTiposIdentificacion() {
    const url = `${this.apiUrl}/${endPoints.EXTERNAL}/documentos/tipos-identificacion`;
    return this.http.get<any>(url).pipe(
      map(({ data }) => data),
      catchError(() => of([]))
    );
  }

  getPermisosPorRolId(id: string) {
    const url = `${this.apiUrl}/${endPoints.EXTERNAL}/permiso/roles/${id}/permisos`;
    return this.http.get<any>(url).pipe(
      map(({ data }) => data),
      catchError(() => of([]))
    );
  }

  startValidateUserContact(tipoMedioContacto: string) {
    const queryParams = new URLSearchParams({ tipoMedioContacto });
    const url = `${this.apiUrl}/${endPoints.INTERNAL
      }/usuario/medio-contacto/iniciar?${queryParams.toString()}`;

    return this.http.post<
      IData<{
        solicitudGestionUsuarioId: number;
        tokenId: string;
      }>
    >(url, null);
  }

  validateUserContact(model: FormData) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/usuario/medio-contacto/validar`;

    model.append('esConfirmarCodigoOTP', JSON.stringify(true));

    return this.http.post<IData<{ otpValidado: boolean }>>(url, model);
  }

  resendUserContactCode(model: FormData) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/usuario/medio-contacto/validar`;

    model.append('esRegenerarCodigoOTP', JSON.stringify(true));

    return this.http
      .post<
        IData<{
          otpRegenerado: {
            solicitudGestionUsuarioId: number;
            tokenId: string;
          };
        }>
      >(url, model)
      .pipe(map((x) => x.data.otpRegenerado));
  }

  /**
   *
   * @returns valor de **data**
   */
  validateBiometricFace(form: FormData) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/biometria/facial`;
    return this.http.post<IData<any>>(url, form);
  }

  validateBiometricFingerPrint(form: FormData) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/biometria/huella`;
    return this.http.post<IData<any>>(url, form);
  }

  getSocketsUrls(tipoIdentificacion: string, numeroIdentificacion: string) {
    const search = new URLSearchParams({ tipoIdentificacion });
    const url = `${this.apiUrl
      }/internal/usuario/${numeroIdentificacion}/validacion-facial/web-socket/generar?${search.toString()}`;
    return this.http.get<
      IData<{
        urlPortal: string;
        urlMovil: string;
      }>
    >(url);
  }

  getBiometricResources(consentimientoId: number) {
    const url = `${this.apiUrl}/internal/usuario/${consentimientoId}/foto-ciudadano/consultar`;
    return this.http.get<IData<string>>(url);
  }
}
